"use client";

import React, { useState } from "react";
import { CircleCheck as CheckCircle2, CircleAlert as AlertCircle, Loader as Loader2, Send } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
export default function RequestEstimate() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    project_type: "",
    contact_method: "",
    timing: "",
    budget: "",
    details: "",
    consent: false
  });
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const handleChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.consent) {
      setErrorMessage("Please agree to the contact terms to proceed.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMessage("");
    try {
      const currentPath = typeof window !== "undefined" ? window.location.pathname : "/";
      const params = new URLSearchParams();
      params.append("name", formData.name);
      params.append("phone", formData.phone);
      params.append("email", formData.email);
      params.append("address", formData.address);
      params.append("project_type", formData.project_type);
      params.append("contact_method", formData.contact_method);
      params.append("timing", formData.timing);
      params.append("budget", formData.budget || "Not specified");
      params.append("details", formData.details || "");
      params.append("consent", formData.consent ? "true" : "false");
      params.append("website", "DecaturHomeRenovation.com");
      params.append("page", currentPath);
      params.append("lead_source", "organic");
      const response = await fetch("https://hooks.zapier.com/hooks/catch/28361868/4mcz4d7/", {
        method: "POST",
        body: params
      });
      if (response.ok) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
      }
    } catch {
      setStatus("error");
      setErrorMessage("Something went wrong. Please call (470) 847-9076 instead.");
    }
  };
  return <section id="requestestimate" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
      {/* Background Architectural Accent Lines */}
      <div className="absolute inset-0 pointer-events-none opacity-40" aria-hidden="true">
        <div className="absolute left-1/2 -top-40 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-secondary/50 blur-3xl" />
      </div>

      <div className="relative max-w-3xl mx-auto z-10">
        {/* Header Area */}
        <div className="text-center mb-10 md:mb-12">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            Request an Estimate
          </h2>
          <div className="w-16 h-0.5 bg-border mx-auto my-4" />
          <p className="text-muted-foreground text-base sm:text-lg max-w-xl mx-auto">
            Tell us about your home and remodeling ideas. We will review your goals and reach out to schedule an in-depth consultation.
          </p>
        </div>

        {/* Form Card */}
        <Card className="border border-border/80 bg-card/85 backdrop-blur-md shadow-lg rounded-xl overflow-hidden">
          {status === "success" ? <CardContent className="py-14 px-6 sm:px-12 text-center flex flex-col items-center">
              <div className="size-16 rounded-full bg-secondary flex items-center justify-center mb-6 text-foreground">
                <CheckCircle2 className="size-10" />
              </div>
              <CardTitle className="text-2xl sm:text-3xl font-serif mb-3 text-foreground">
                Request Received
              </CardTitle>
              <CardDescription className="text-base text-muted-foreground max-w-md mb-8">
                Thank you for reaching out. Our design and estimation team has received your project details and will contact you via your preferred method shortly.
              </CardDescription>
              <Button variant="outline" onClick={() => {
            setFormData({
              name: "",
              phone: "",
              email: "",
              address: "",
              project_type: "",
              contact_method: "",
              timing: "",
              budget: "",
              details: "",
              consent: false
            });
            setStatus("idle");
          }}>
                Submit another request
              </Button>
            </CardContent> : <>
              <CardHeader className="pt-8 pb-4 px-6 sm:px-10 border-b border-border/40">
                <CardTitle className="text-xl font-medium text-foreground">
                  Project Inquiry Details
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  Fields marked with an asterisk (<span className="text-destructive">*</span>) are required.
                </CardDescription>
              </CardHeader>

              <CardContent className="pt-6 pb-8 px-6 sm:px-10">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {status === "error" && errorMessage && <Alert variant="destructive">
                      <AlertCircle className="size-4" />
                      <AlertTitle>Notice</AlertTitle>
                      <AlertDescription>{errorMessage}</AlertDescription>
                    </Alert>}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Full Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name">
                        Full Name <span className="text-destructive">*</span>
                      </Label>
                      <Input id="name" type="text" required value={formData.name} onChange={e => handleChange("name", e.target.value)} placeholder="John Doe" disabled={status === "submitting"} />
                    </div>

                    {/* Phone Number */}
                    <div className="space-y-2">
                      <Label htmlFor="phone">
                        Phone Number <span className="text-destructive">*</span>
                      </Label>
                      <Input id="phone" type="tel" required value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="Your phone number" disabled={status === "submitting"} />
                    </div>

                    {/* Email Address */}
                    <div className="space-y-2">
                      <Label htmlFor="email">
                        Email Address <span className="text-destructive">*</span>
                      </Label>
                      <Input id="email" type="email" required value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="Your email address" disabled={status === "submitting"} />
                    </div>

                    {/* Street Address or ZIP */}
                    <div className="space-y-2">
                      <Label htmlFor="address">
                        Street Address or ZIP Code <span className="text-destructive">*</span>
                      </Label>
                      <Input id="address" type="text" required value={formData.address} onChange={e => handleChange("address", e.target.value)} placeholder="e.g. 30030 or 123 Main St" disabled={status === "submitting"} />
                    </div>

                    {/* Project Type */}
                    <div className="space-y-2">
                      <Label htmlFor="project_type">
                        Project Type <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.project_type} onValueChange={val => handleChange("project_type", val)} required disabled={status === "submitting"}>
                        <SelectTrigger id="project_type" className="w-full">
                          <SelectValue placeholder="Select a project category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Kitchen Remodeling">Kitchen Remodeling</SelectItem>
                          <SelectItem value="Bathroom Remodeling">Bathroom Remodeling</SelectItem>
                          <SelectItem value="Whole-Home Remodeling">Whole-Home Remodeling</SelectItem>
                          <SelectItem value="Home Additions">Home Additions</SelectItem>
                          <SelectItem value="Basement Remodeling">Basement Remodeling</SelectItem>
                          <SelectItem value="Interior Remodeling">Interior Remodeling</SelectItem>
                          <SelectItem value="Outdoor Living">Outdoor Living</SelectItem>
                          <SelectItem value="Deck Remodeling">Deck Remodeling</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Preferred Contact Method */}
                    <div className="space-y-2">
                      <Label htmlFor="contact_method">
                        Preferred Contact Method <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.contact_method} onValueChange={val => handleChange("contact_method", val)} required disabled={status === "submitting"}>
                        <SelectTrigger id="contact_method" className="w-full">
                          <SelectValue placeholder="Choose contact method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Phone">Phone</SelectItem>
                          <SelectItem value="Text">Text</SelectItem>
                          <SelectItem value="Email">Email</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Estimated Timing */}
                    <div className="space-y-2">
                      <Label htmlFor="timing">
                        Estimated Timing <span className="text-destructive">*</span>
                      </Label>
                      <Select value={formData.timing} onValueChange={val => handleChange("timing", val)} required disabled={status === "submitting"}>
                        <SelectTrigger id="timing" className="w-full">
                          <SelectValue placeholder="When would you like to begin?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="ASAP">ASAP</SelectItem>
                          <SelectItem value="Within 1 month">Within 1 month</SelectItem>
                          <SelectItem value="1-3 months">1-3 months</SelectItem>
                          <SelectItem value="3-6 months">3-6 months</SelectItem>
                          <SelectItem value="Just researching">Just researching</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Approximate Budget */}
                    <div className="space-y-2">
                      <Label htmlFor="budget">Approximate Budget (Optional)</Label>
                      <Select value={formData.budget} onValueChange={val => handleChange("budget", val)} disabled={status === "submitting"}>
                        <SelectTrigger id="budget" className="w-full">
                          <SelectValue placeholder="Select estimated budget" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Under $25,000">Under $25,000</SelectItem>
                          <SelectItem value="$25,000-$50,000">$25,000-$50,000</SelectItem>
                          <SelectItem value="$50,000-$100,000">$50,000-$100,000</SelectItem>
                          <SelectItem value="$100,000+">$100,000+</SelectItem>
                          <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Project Details */}
                  <div className="space-y-2">
                    <Label htmlFor="details">Project Details (Optional)</Label>
                    <Textarea id="details" rows={4} value={formData.details} onChange={e => handleChange("details", e.target.value)} placeholder="Share any specific goals, measurements, architectural features, or architectural requirements for your project..." disabled={status === "submitting"} />
                  </div>

                  {/* Consent Checkbox */}
                  <div className="flex items-start gap-3 pt-2">
                    <Checkbox id="consent" checked={formData.consent} onCheckedChange={checked => handleChange("consent", checked === true)} required disabled={status === "submitting"} className="mt-1" />
                    <Label htmlFor="consent" className="text-xs sm:text-sm font-normal text-muted-foreground leading-relaxed cursor-pointer">
                      I agree to be contacted about my project by phone, text, or email.{" "}
                      <span className="text-destructive">*</span>
                    </Label>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-3">
                    <Button type="submit" size="lg" className="w-full font-medium tracking-wide transition-all shadow-md hover:shadow-lg" disabled={status === "submitting"}>
                      {status === "submitting" ? <>
                          <Loader2 className="size-4 animate-spin" />
                          Submitting Estimate Request...
                        </> : <>
                          <Send className="size-4" />
                          Send Estimate Request
                        </>}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </>}
        </Card>
      </div>
    </section>;
}